import { Product } from "@omni/types/api/product/bff/product.model";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { isDefined } from "@omni/utils/helper.utils";
import {
    PropsWithChildren,
    SyntheticEvent,
    useCallback,
    useMemo,
    useState
} from "react";

import { Modal } from "@/components/General/Modal/modal";
import { AddToCardModalFooter } from "@/components/Modals/AddToCartModal/components/AddToCart/Partials/footer";
import { StorePickerModal } from "@/components/Store/StorePicker/storePickerModal.container";
import { StorePickerFeature } from "@/components/Store/StorePicker/types/storePickerFeature.enum";
import { StorePickerType } from "@/components/Store/StorePicker/types/storePickerType.enum";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { GetStoreStrategy } from "@/services/store.service";
import { StorePickerModalStates } from "@/utils/enums";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";
import { useStoreCode } from "@/utils/hooks/store/useStoreCode.hook";

import { AddToCartButtonComponent } from "./addToCartButton.component";

type AddToCartButtonProps = PropsWithChildren<{
    addToCartItems: Array<{
        sku: string;
        amount: number;
        product?: Product<ProductFieldSet.ProductTile>;
    }>;
}>;

const AddToCartButton = ({
    addToCartItems,
    children
}: AddToCartButtonProps) => {
    const dispatch = useThunkDispatch();
    const { t } = useGranite();

    const isClient = useIsClientSide();

    const storeCode = useStoreCode({ strategy: GetStoreStrategy.Stock });

    const [storePickerModalState, setStorePickerModalState] = useState(
        StorePickerModalStates.Closed
    );

    const [displayAddToCartModal, setDisplayAddToCartModal] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const handleAction = useCallback(
        (storeCode: string | undefined) => {
            if (!isDefined(storeCode)) {
                return setStorePickerModalState(StorePickerModalStates.Open);
            }

            const { requestItems, trackingItems } = addToCartItems.reduce<{
                requestItems: {
                    sku: string;
                    quantity: number;
                    deliveryType: DeliveryType;
                }[];
                trackingItems: {
                    product?: Product<ProductFieldSet.ProductTile>;
                    quantity: number;
                }[];
            }>(
                (items, { sku, amount, product }) => {
                    items.requestItems.push({
                        sku: sku,
                        quantity: amount,
                        deliveryType: DeliveryType.ClickAndCollect
                    });
                    items.trackingItems.push({
                        product: product,
                        quantity: amount
                    });

                    return items;
                },
                { requestItems: [], trackingItems: [] }
            );

            dispatch(
                CartModule.addToCartBulk(
                    {
                        shopId: storeCode,
                        items: requestItems
                    },
                    trackingItems,
                    true
                )
            ).then((action) => {
                if (action.type === CartModule.APIStatus.SUCCESS) {
                    setDisplayAddToCartModal(true);
                }
            });
        },
        [setStorePickerModalState, dispatch]
    );

    const handleOnClick = useCallback(
        (event: SyntheticEvent<HTMLButtonElement>) => {
            event.preventDefault();
            handleAction(storeCode);
        },
        [storeCode, handleAction]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const handleOnStorePickerModalClose = useCallback(
        () => setStorePickerModalState(StorePickerModalStates.Closed),
        [setStorePickerModalState]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const handleOnStorePickerModalSubmit = useCallback(
        async (store: CloudStore, onClose?: () => void) => {
            handleAction(store.code);
            onClose?.();
        },
        []
    );

    const productIsClickAndCollect = useMemo(
        () =>
            addToCartItems.some(
                (cartItem) =>
                    !!cartItem.product?.deliveryTypes.includes(
                        DeliveryType.ClickAndCollect
                    )
            ),
        [addToCartItems]
    );

    if (!isClient) return null;

    return (
        <>
            <AddToCartButtonComponent onClick={handleOnClick}>
                {children}
            </AddToCartButtonComponent>
            <StorePickerModal
                type={StorePickerType.SELECT}
                isOpen={storePickerModalState !== StorePickerModalStates.Closed}
                storeCode={storeCode ?? null}
                submitButtonText={t(
                    "general.storepicker.button.confirmStoreAndOrder"
                )}
                infoLink={null}
                fixedFeatures={
                    productIsClickAndCollect
                        ? [StorePickerFeature.BUY_ONLINE_PICKUP_IN_STORE]
                        : []
                }
                displayStockLevel={false}
                onSubmit={handleOnStorePickerModalSubmit}
                onClose={handleOnStorePickerModalClose}
            />
            <Modal
                title={
                    <h2
                        className="text-secondary"
                        style={{ wordBreak: "break-word" }}
                    >
                        <i
                            className="las la-cart-arrow-down"
                            aria-hidden="true"
                        ></i>
                        {addToCartItems.length > 1
                            ? t("cta.addtocart.multi.modal.title")
                            : t("cta.addtocart.modal.title")}
                    </h2>
                }
                open={displayAddToCartModal}
                setOpen={() => setDisplayAddToCartModal(false)}
                withFooter={false}
            >
                <AddToCardModalFooter
                    onModalClose={() => setDisplayAddToCartModal(false)}
                />
            </Modal>
        </>
    );
};

export { AddToCartButton };
